import React, { useMemo } from "react";
import {
  DeploymentResponseDto,
  ModelResponseDto,
  PredictionRunItem,
  ProjectResponseDto,
} from "../common/ObjectTypes";
import SelectInput from "../common/SelectInput";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "../common/TailwindClassnames";
import { BeatLoader } from "react-spinners";
import { RefreshIcon } from "../common/ProjectIcons";
import ConfirmationModal from "../common/ConfirmationModal";
import StageCard from "./StageCard";
import InferenceStagePanel from "./InferenceStagePanel";
import PipelineSummaryPanel from "./PipelineSummaryPanel";
import PredictionRunHistory from "./PredictionRunHistory";

type RunPagination = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
};

type RunSort = {
  sortBy: string;
  sortDirection: "asc" | "desc";
  onSort: (column: string) => void;
};

type ConfirmAction = "detection" | "classification" | "pipeline" | null;

type AiModelZooProps = {
  projects: ProjectResponseDto[];
  selectedProject: ProjectResponseDto | null;
  loading: boolean;
  deploymentsLoading?: boolean;
  resultsLoaded: boolean;
  deployments: DeploymentResponseDto[];
  onSelectProject: (
    project: ProjectResponseDto | null,
    redirect: boolean,
    removeLastSelectedDeployment?: boolean,
  ) => void;
  onSelectDeployment: (
    deployment: DeploymentResponseDto | null,
    redirect: boolean,
  ) => void;
  selectedDeployment: DeploymentResponseDto | null;
  /** True when the Deployment selector is on "All deployments" (no single deployment picked). */
  isAllDeployments: boolean;
  detectionModels: ModelResponseDto[];
  classifierModels: ModelResponseDto[];
  selectedDetectionModel: ModelResponseDto | null;
  selectedClassificationModel: ModelResponseDto | null;
  onSelectDetectionModel: (model: ModelResponseDto | null) => void;
  onSelectClassificationModel: (model: ModelResponseDto | null) => void;
  modelsLoading: boolean;
  filesInAkili: number | null;
  loadingFilesInAkili: boolean;
  detecting: boolean;
  classifying: boolean;
  predictionRuns: PredictionRunItem[];
  loadingPredictionRuns: boolean;
  projectPredictionRuns: PredictionRunItem[];
  loadingProjectPredictionRuns: boolean;
  onRefreshDeploymentRuns: () => void;
  onRefreshProjectRuns: () => void;
  projectRunPagination: RunPagination;
  deploymentRunPagination: RunPagination;
  projectRunSort: RunSort;
  deploymentRunSort: RunSort;
  onDetectionConfThreshChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onClassificationConfThreshChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  importOrUploadInProgress: boolean;
  inferenceInProgress: boolean;
  onRetryFailedRun: (run: PredictionRunItem) => void;
  retryingRunIds: Set<number>;
  detectionConfThreshError: string;
  classificationConfThreshError: string;
  confirmAction: ConfirmAction;
  onOpenConfirm: (action: ConfirmAction) => void;
  onCloseConfirm: () => void;
  onConfirmRun: () => void;
};

/**
 * The model's own default confidence threshold, as a percentage string.
 *
 * The selected-model objects in page state carry the **edited** threshold —
 * the page seeds them through `applyDefaultThresh` and the threshold input
 * writes back into them — so anything describing the model's default has to
 * read the untouched entry from the fetched list, where it is still a 0-1
 * fraction.
 *
 * @param model the (possibly edited) selected model
 * @param originalModels the fetched, unedited list to look the default up in
 * @returns the default as a percentage string, or undefined if unknown
 */
const modelDefaultPct = (
  model: ModelResponseDto | null,
  originalModels: ModelResponseDto[],
): string | undefined => {
  if (!model) return undefined;
  const raw = originalModels.find(
    (m) => m.model_id === model.model_id,
  )?.default_confidence_threshold;
  if (raw === undefined || raw === null || raw === "") return undefined;
  const pct = Number(raw) * 100;
  return Number.isNaN(pct) ? undefined : String(pct);
};

/**
 * The threshold a run would actually use, as a percentage string.
 *
 * Clearing the input is supported and means "use the model default" —
 * `resolveConfThresh` on the page sends the default in that case — so the
 * summaries and confirmation copy have to resolve it the same way instead of
 * rendering an empty "@ %".
 *
 * @param model the (possibly edited) selected model
 * @param originalModels the fetched, unedited list to fall back to
 * @returns the effective threshold as a percentage string ("" if unknown)
 */
const effectiveThreshPct = (
  model: ModelResponseDto | null,
  originalModels: ModelResponseDto[],
): string => {
  if (!model) return "";
  const edited = model.default_confidence_threshold;
  if (edited !== undefined && edited !== null && edited !== "")
    return String(edited);
  return modelDefaultPct(model, originalModels) ?? "";
};

const AiModelZooDetails: React.FC<AiModelZooProps> = ({
  projects,
  classifierModels,
  detectionModels,
  selectedProject,
  loading,
  selectedDetectionModel,
  onSelectDetectionModel,
  deploymentsLoading = false,
  resultsLoaded,
  deployments = [],
  onSelectProject,
  onSelectDeployment,
  selectedDeployment,
  isAllDeployments,
  modelsLoading = false,
  onSelectClassificationModel,
  selectedClassificationModel,
  filesInAkili,
  loadingFilesInAkili,
  detecting,
  classifying,
  predictionRuns,
  loadingPredictionRuns,
  projectPredictionRuns,
  loadingProjectPredictionRuns,
  onRefreshDeploymentRuns,
  projectRunPagination,
  deploymentRunPagination,
  onDetectionConfThreshChange,
  onClassificationConfThreshChange,
  importOrUploadInProgress,
  inferenceInProgress,
  onRetryFailedRun,
  retryingRunIds,
  onRefreshProjectRuns,
  projectRunSort,
  deploymentRunSort,
  detectionConfThreshError,
  classificationConfThreshError,
  confirmAction,
  onOpenConfirm,
  onCloseConfirm,
  onConfirmRun,
}) => {
  const { t, i18n } = useTranslation();

  const anythingInProgress = importOrUploadInProgress || inferenceInProgress;

  // Grouped against the language the UI is actually in, not the browser's —
  // a bare toLocaleString() would print English separators inside a French,
  // Spanish or German page. Matches MigrationReport's formatNumber().
  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language),
    [i18n.language],
  );
  const num = (n: number) => numberFormatter.format(n);

  const sortedProjects = useMemo(
    () =>
      [...projects].sort((a, b) =>
        a.project_name.localeCompare(b.project_name),
      ),
    [projects],
  );

  const sortedDeployments = useMemo(
    () =>
      [...deployments].sort((a, b) =>
        a.deployment_name.localeCompare(b.deployment_name),
      ),
    [deployments],
  );

  const totalFiles = isAllDeployments
    ? deployments.reduce((sum, d) => sum + (d.media_asset_count || 0), 0)
    : (filesInAkili ?? 0);

  const filesLoading = isAllDeployments
    ? deploymentsLoading
    : loadingFilesInAkili;

  const hasFiles =
    selectedProject !== null &&
    !filesLoading &&
    (isAllDeployments ? deployments.length > 0 : selectedDeployment !== null) &&
    totalFiles > 0;

  const scopeLabel = isAllDeployments
    ? t("Whole project ({{count}} deployments)", { count: deployments.length })
    : (selectedDeployment?.deployment_name ?? "");

  const inProgressMessage = importOrUploadInProgress
    ? t(
        "An import or upload is currently in progress for this selection. Please try again when it is complete.",
      )
    : inferenceInProgress
      ? t(
          "An inference task is currently in progress for this selection. Please try again when it is complete.",
        )
      : undefined;

  // `filesLoading` gates every run action: the page does not clear
  // `filesInAkili` while it refetches, so between switching deployments and
  // the count arriving, `totalFiles` still holds the previous deployment's
  // number — enough to leave these predicates false and let a run be
  // confirmed against a count the label and dialog have already gone stale on.
  const detectionRunDisabled =
    !selectedDetectionModel ||
    !!detectionConfThreshError ||
    anythingInProgress ||
    filesLoading ||
    totalFiles === 0;

  const classificationRunDisabled =
    !selectedClassificationModel ||
    !!classificationConfThreshError ||
    anythingInProgress ||
    filesLoading ||
    totalFiles === 0;

  const pipelineDisabled =
    !selectedDetectionModel ||
    !selectedClassificationModel ||
    !!detectionConfThreshError ||
    !!classificationConfThreshError ||
    anythingInProgress ||
    filesLoading ||
    totalFiles === 0;

  const detectionThreshPct = effectiveThreshPct(
    selectedDetectionModel,
    detectionModels,
  );
  const classificationThreshPct = effectiveThreshPct(
    selectedClassificationModel,
    classifierModels,
  );

  const detectionSummary = selectedDetectionModel
    ? `${selectedDetectionModel.name} @ ${detectionThreshPct}%`
    : t("Not configured");
  const classificationSummary = selectedClassificationModel
    ? `${selectedClassificationModel.name} @ ${classificationThreshPct}%`
    : t("Not configured");

  // Both prerequisites get their own hint: the pipeline stays disabled until
  // each stage has a model, and picking classification first used to leave
  // this saying "Queues detection…" beside a button that could not run.
  const pipelineHint = !selectedDetectionModel
    ? t("Select a detection model to enable the pipeline.")
    : !selectedClassificationModel
      ? t("Select a classification model to enable the pipeline.")
      : t("Queues detection, then classification on the results.");

  // ── Confirmation dialog copy ───────────────────────────────────────────
  const confirmCfg = (() => {
    // Resolved the same way the request itself resolves it, so a cleared
    // threshold confirms the model default that will actually be sent rather
    // than an empty "at a % confidence threshold".
    const detThresh = detectionThreshPct;
    const clsThresh = classificationThreshPct;
    if (confirmAction === "detection" && selectedDetectionModel) {
      return {
        title: t("Start detection?"),
        body: t(
          "Detection will process {{n}} photos from {{scope}} using {{model}} at a {{thresh}}% confidence threshold.",
          {
            n: num(totalFiles),
            scope: scopeLabel,
            model: selectedDetectionModel.name,
            thresh: detThresh,
          },
        ),
        cta: t("Queue detection"),
      };
    }
    if (confirmAction === "classification" && selectedClassificationModel) {
      return {
        title: t("Start classification?"),
        body: t(
          "Classification will process {{n}} photos from {{scope}} using {{model}} at a {{thresh}}% confidence threshold.",
          {
            n: num(totalFiles),
            scope: scopeLabel,
            model: selectedClassificationModel.name,
            thresh: clsThresh,
          },
        ),
        cta: t("Queue classification"),
      };
    }
    if (
      confirmAction === "pipeline" &&
      selectedDetectionModel &&
      selectedClassificationModel
    ) {
      return {
        title: t("Start full pipeline?"),
        body: t(
          "Detection will process {{n}} photos from {{scope}} using {{detModel}} at {{detThresh}}%. When it completes, classification will run on the results using {{clsModel}} at {{clsThresh}}%.",
          {
            n: num(totalFiles),
            scope: scopeLabel,
            detModel: selectedDetectionModel.name,
            detThresh: detThresh,
            clsModel: selectedClassificationModel.name,
            clsThresh: clsThresh,
          },
        ),
        cta: t("Queue both runs"),
      };
    }
    return null;
  })();

  const confirmNote = t(
    "The job is queued and usually starts within a minute. You can track its progress in Run history.",
  );

  return (
    <div className="flex flex-row w-full">
      <div className="ml-8 mr-4 mt-8 w-full">
        <div className={tailwindClassnames.pageHeaderRow}>
          <div>
            <h1 className={tailwindClassnames.pageTitle}>
              {t("AI Processing")}
            </h1>
            <p className={tailwindClassnames.pageSubtitle}>
              {t(
                "Run detection and classification on your camera-trap photos, then track every run below.",
              )}
            </p>
          </div>
        </div>

        {/* ── Stage 1: Choose photos ── */}
        <StageCard
          stepNumber={1}
          title={t("Choose photos")}
          description={t(
            "Pick a deployment, or process every deployment in the project at once.",
          )}
        >
          <div className="flex flex-row flex-wrap gap-4 items-start">
            <div className="flex-1 min-w-[280px] max-w-md">
              <SelectInput
                options={sortedProjects.map((project) => ({
                  value: project,
                  label: project.project_name,
                }))}
                className="w-full bg-storm-blue rounded h-full shadow text-left text-sm"
                isDisabled={false}
                isLoading={loading}
                isClearable={true}
                isSearchable={true}
                value={
                  selectedProject === null
                    ? null
                    : {
                        value: selectedProject,
                        label: selectedProject.project_name,
                      }
                }
                isMulti={false}
                name={t("Project")}
                onChange={async (e: any) => {
                  await onSelectProject(e ? e.value : null, true, true);
                }}
                errors=""
                searchBar={false}
              />
            </div>
            {selectedProject &&
            !deploymentsLoading &&
            deployments.length === 0 ? (
              <div className="mt-4">
                <p className="text-sm text-primary-off-black dark:text-primary-off-white">
                  {t("No deployments found for this project.")}
                </p>
              </div>
            ) : (
              <div className="flex-1 min-w-[280px] max-w-md">
                <SelectInput
                  options={[
                    {
                      value: null,
                      label: t("All deployments ({{count}})", {
                        count: deployments.length,
                      }),
                    },
                    ...sortedDeployments.map((deployment) => ({
                      value: deployment,
                      label: deployment.deployment_name,
                    })),
                  ]}
                  className="w-full bg-storm-blue rounded h-full shadow text-left text-sm"
                  isDisabled={!selectedProject}
                  isLoading={deploymentsLoading}
                  isClearable={false}
                  isSearchable={true}
                  value={
                    selectedDeployment === null
                      ? {
                          value: null,
                          label: t("All deployments ({{count}})", {
                            count: deployments.length,
                          }),
                        }
                      : {
                          value: selectedDeployment,
                          label: selectedDeployment.deployment_name,
                        }
                  }
                  isMulti={false}
                  name={t("Deployment")}
                  onChange={(e: any) => {
                    onSelectDeployment(e ? e.value : null, true);
                  }}
                  errors=""
                  searchBar={false}
                />
              </div>
            )}
            {selectedProject && !isAllDeployments && selectedDeployment && (
              <div className="mt-4 flex flex-col min-w-[220px]">
                <span className="text-left text-sm text-gray-500">
                  {t("Number of Files in Akili:")}
                  <span className="ml-3 text-lg font-semibold text-off-black-light dark:text-off-white-dark">
                    {loadingFilesInAkili ? (
                      <BeatLoader
                        color={tailwindClassnames.skyBlueDark}
                        loading={true}
                        size={15}
                        margin={5}
                      />
                    ) : (
                      filesInAkili
                    )}
                  </span>
                </span>
              </div>
            )}
          </div>
        </StageCard>

        {selectedProject && (deploymentsLoading || deployments.length > 0) && (
          <>
            {/* ── Stage 2: Detection ── */}
            <StageCard
              stepNumber={2}
              title={t("Detection")}
              description={t(
                "Detection finds animals, people and vehicles in each photo. Classification (below) then names the species.",
              )}
            >
              <InferenceStagePanel
                thresholdInputId="det-thresh"
                models={detectionModels}
                modelsLoading={modelsLoading}
                selectedModel={selectedDetectionModel}
                onSelectModel={onSelectDetectionModel}
                confThreshError={detectionConfThreshError}
                onConfThreshChange={onDetectionConfThreshChange}
                hint={t(
                  "Lower values find more possible animals but may produce more false positives.",
                )}
                disabled={modelsLoading}
                isRunning={detecting}
                runDisabled={detectionRunDisabled}
                runLabel={t("Run detection on {{n}} photos", {
                  n: num(totalFiles),
                })}
                onRun={() => onOpenConfirm("detection")}
                blockReason={inProgressMessage}
              />
            </StageCard>

            {/* ── Stage 3: Classification ── */}
            <StageCard
              stepNumber={3}
              title={t("Classification")}
              description={t(
                "Classification identifies the species in photos where detection already found an animal.",
              )}
              titleExtra={
                <span className="text-xs text-off-black-light dark:text-greyed-text border border-off-white-dark dark:border-storm-blue-border rounded-full px-2.5 py-0.5">
                  {t("Requires detection results")}
                </span>
              }
            >
              <InferenceStagePanel
                thresholdInputId="cls-thresh"
                models={classifierModels}
                modelsLoading={modelsLoading}
                selectedModel={selectedClassificationModel}
                onSelectModel={onSelectClassificationModel}
                confThreshError={classificationConfThreshError}
                onConfThreshChange={onClassificationConfThreshChange}
                hint={t(
                  "Species below this confidence are left unlabeled for manual review.",
                )}
                disabled={modelsLoading}
                isRunning={classifying}
                runDisabled={classificationRunDisabled}
                runLabel={t("Run classification on {{n}} photos", {
                  n: num(totalFiles),
                })}
                onRun={() => onOpenConfirm("classification")}
                blockReason={inProgressMessage}
              />
            </StageCard>

            {/* ── What will run / Run full pipeline ── */}
            <PipelineSummaryPanel
              photosFmt={num(totalFiles)}
              scopeLabel={scopeLabel}
              detectionSummary={detectionSummary}
              classificationSummary={classificationSummary}
              pipelineHint={pipelineHint}
              pipelineDisabled={pipelineDisabled}
              onRunPipeline={() => onOpenConfirm("pipeline")}
              blockReason={inProgressMessage}
            />
          </>
        )}

        {selectedProject &&
          !filesLoading &&
          !hasFiles &&
          deployments.length > 0 && (
            <div className="mt-4 mb-4">
              <p className="text-sm text-primary-off-black dark:text-primary-off-white">
                {t("No files in Akili to run inference on.")}
              </p>
            </div>
          )}

        {/* ── Run history ── */}
        {selectedProject && (
          <div
            className={`${tailwindClassnames.formSectionCard} mb-5 text-left`}
          >
            <div
              className={`${tailwindClassnames.formSectionHeader} flex items-center gap-3`}
            >
              <h2 className={tailwindClassnames.formSectionTitle}>
                {t("Run History")}
              </h2>
              <button
                onClick={
                  isAllDeployments
                    ? onRefreshProjectRuns
                    : onRefreshDeploymentRuns
                }
                disabled={
                  isAllDeployments
                    ? loadingProjectPredictionRuns
                    : loadingPredictionRuns || !selectedDeployment
                }
                className="p-1 rounded transition duration-300 dark:hover:text-storm-blue hover:bg-off-white-dark dark:hover:bg-storm-blue"
                aria-label={t("Refresh")}
              >
                <RefreshIcon />
              </button>
            </div>
            <PredictionRunHistory
              predictionRuns={
                isAllDeployments ? projectPredictionRuns : predictionRuns
              }
              loading={
                isAllDeployments
                  ? loadingProjectPredictionRuns
                  : loadingPredictionRuns
              }
              showDeployment={isAllDeployments}
              showFileCount
              page={
                isAllDeployments
                  ? projectRunPagination.page
                  : deploymentRunPagination.page
              }
              pageSize={
                isAllDeployments
                  ? projectRunPagination.pageSize
                  : deploymentRunPagination.pageSize
              }
              totalCount={
                isAllDeployments
                  ? projectRunPagination.total
                  : deploymentRunPagination.total
              }
              onPageChange={
                isAllDeployments
                  ? projectRunPagination.onPageChange
                  : deploymentRunPagination.onPageChange
              }
              onPageSizeChange={
                isAllDeployments
                  ? projectRunPagination.onPageSizeChange
                  : deploymentRunPagination.onPageSizeChange
              }
              sortBy={
                isAllDeployments
                  ? projectRunSort.sortBy
                  : deploymentRunSort.sortBy
              }
              sortDirection={
                isAllDeployments
                  ? projectRunSort.sortDirection
                  : deploymentRunSort.sortDirection
              }
              onSort={
                isAllDeployments
                  ? projectRunSort.onSort
                  : deploymentRunSort.onSort
              }
              onRetryFailedRun={onRetryFailedRun}
              retryingRunIds={retryingRunIds}
              contextLabel={
                isAllDeployments
                  ? t("Showing AI processing history for Project: {{name}}", {
                      name: selectedProject.project_name,
                    })
                  : selectedDeployment
                    ? t(
                        "Showing AI processing history for Deployment: {{name}}",
                        { name: selectedDeployment.deployment_name },
                      )
                    : undefined
              }
              emptyMessage={
                isAllDeployments
                  ? t("No AI processing history found for this project.")
                  : t("No AI processing history found for this deployment.")
              }
            />
          </div>
        )}

        {!loading && resultsLoaded && projects.length === 0 && (
          <div className="mt-4">
            <p className="text-sm text-primary-off-black dark:text-primary-off-white">
              {t(
                "You don't have access to any projects with inference permissions.",
              )}
            </p>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={!!confirmAction}
        title={confirmCfg?.title}
        confirmationMessage={
          confirmCfg ? `${confirmCfg.body} ${confirmNote}` : ""
        }
        confirmLabel={confirmCfg?.cta}
        onConfirm={onConfirmRun}
        onCancel={onCloseConfirm}
      />
    </div>
  );
};

export default AiModelZooDetails;
